/**
 * Student Report — renders EXCLUSIVELY from the Experience Engine payload
 * persisted in `learner_reports` (audience = 'student'). No legacy
 * calculations, no mock data. If a persisted report exists, it is shown
 * verbatim. If not, `report-assemble` is invoked with persist:true to
 * create one, and the returned payload is shown.
 *
 * Sprint 1 Phase 2 — Living Report™: this page owns data and states only;
 * the presentation is the chaptered journey in
 * `src/components/report/LivingReport.tsx` (Letter → Your Story™ →
 * Career DNA™ → Strengths → Growth → Matches → One Next Step™).
 * The sealed payload is passed through verbatim.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Sparkles, AlertCircle, RefreshCw, Printer, Save } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { consumeStudentReport } from "@/lib/reportPrefetch";
import { supabase } from "@/integrations/supabase/client";
import LivingReport from "@/components/report/LivingReport";
import DnaSurface from "@/components/report/DnaSurface";
import AppShell from "@/components/shell/AppShell";
import type { StudentPayload } from "@/components/report/reportTypes";
import RoadmapPanel from "@/components/roadmap/RoadmapPanel";
import { isPreviewMode, previewStudentPayload } from "@/lib/previewMode";
import { recordRouteHit } from "@/lib/routeDiag";
import { ga4Once } from "@/lib/ga4";
import {
  buildDerivedStudentPayload, mergeStudentPayloads, sealedPayloadIsThin,
} from "@/features/assessment-v2/derivedPayload";
import { loadLatestCompletedRun } from "@/features/assessment-v2/persistedSnapshot";
import { loadCareersBundle } from "@/data/careersBundle";
import { loadMarketIntelligence } from "@/lib/marketIntelligence";
import { snapshotReport } from "@/lib/reportSnapshots";
import ReportHistory from "@/components/report/ReportHistory";
import SavedReportRoadmaps from "@/components/report/SavedReportRoadmaps";
import CollegePlanSection from "@/components/report/CollegePlanSection";
import CounsellorRecommendations from "@/components/report/CounsellorRecommendations";

// Last report shown on this device, so a repeat visit opens instantly while
// the fresh copy loads behind it. Per account; cleared by a newer report.
const reportCacheKey = (id: string) => `wekit_report_cache_v1:${id}`;
function readCachedReport(id: string): StudentPayload | null {
  try { const raw = localStorage.getItem(reportCacheKey(id)); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
}
function writeCachedReport(id: string, p: StudentPayload) {
  try { localStorage.setItem(reportCacheKey(id), JSON.stringify(p)); } catch { /* quota */ }
}


export default function Report() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<(StudentPayload & { derived?: boolean }) | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [historical, setHistorical] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [saving, setSaving] = useState(false);

  async function saveVersion(p: StudentPayload) {
    if (!user?.id || sealedPayloadIsThin(p)) return;
    setSaving(true);
    setSaveStatus("Saving complete version…");
    try {
      const run = await loadLatestCompletedRun();
      const { data, error } = await supabase.functions.invoke("report-save", {
        body: { payload: snapshotReport(p, run), ...(run ? { run_id: run.sessionId } : {}) },
      });
      if (error || (!data?.saved && data?.reason !== "exists")) throw new Error("save_failed");
      if (data?.payload) { setPayload(data.payload); writeCachedReport(user.id, data.payload); }
      setSaveStatus(data?.saved ? "Complete version saved" : "This version is already saved");
      setHistoryRefresh(n => n + 1);
    } catch { setSaveStatus("Your report is open, but this version couldn’t be saved. Try saving again."); }
    finally { setSaving(false); }
  }

  useEffect(() => { recordRouteHit("Report", "/report served the report link"); }, []);
  useEffect(() => {
    if (payload && user?.id) ga4Once("assessment_completed", { source: "report" });
  }, [payload, user?.id]);

  useEffect(() => {
    let cancelled = false;
    const cached = user?.id ? readCachedReport(user.id) : null;
    if (cached) { setPayload(cached); setLoading(false); } else setLoading(true);
    setError(null);
    setHistorical(false);
    (async () => {
      if (!user?.id) {
        // Dev-only preview door: render the surface with demo data.
        if (isPreviewMode()) {
          setPayload(previewStudentPayload() as unknown as StudentPayload);
          setLoading(false);
        }
        return;
      }
      try {
        // Same fetch as always (persisted report, else assemble) — but if the
        // Mirror Moment™ prefetched it during the pause, this resolves
        // instantly. Single-use cache: later visits always fetch fresh.
        let sealed: StudentPayload | null = null;
        // Start everything the fallback needs now, alongside the saved report,
        // instead of one step after another.
        const runP = loadLatestCompletedRun().catch(() => null);
        void loadCareersBundle().catch(() => null);
        void loadMarketIntelligence().catch(() => null);
        try {
          sealed = (await consumeStudentReport(user.id)) as StudentPayload;
        } catch (e) {
          // A missing seal is not a dead end — the live layer below can still
          // render a complete report from the learner's own answers.
          sealed = null;
          if (!import.meta.env.PROD) console.warn("[report] seal unavailable", e);
        }
        const completedRun = await runP;
        // Never attach a previous run's evidence to a newly completed assessment.
        if (completedRun && sealed?.saved_copy?.run_id && sealed.saved_copy.run_id !== completedRun.sessionId) sealed = null;
        // The seal is the authority. When the pipeline hasn't written the
        // engine rows yet, every section comes back empty — so fill the gaps
        // from the same evidence and the same scoring the results screen uses.
        if (sealedPayloadIsThin(sealed)) {
          const run = completedRun;
          const derived = await buildDerivedStudentPayload({
            learnerId: user.id,
            state: run?.state ?? null,
            displayName:
              (user.user_metadata?.full_name as string | undefined)
              ?? sealed?.learner?.display_name
              ?? null,
          });


          if (!cancelled) {
            const next = derived ? mergeStudentPayloads(sealed, derived) : sealed;
            if (next) { setPayload(next); writeCachedReport(user.id, next); }
            // Keep changed complete copies without replacing any previous version.
            if (derived && next) {
              void saveVersion(next);
            }
            else if (!next && !cached) setError("No completed assessment found for this account yet.");
          }
        } else if (!cancelled) {
          setPayload(sealed);
          if (sealed) writeCachedReport(user.id, sealed);
          if (sealed) void saveVersion(sealed);
        }
      } catch (e) {
        if (!cancelled && !cached) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, attempt]);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-4 text-muted-foreground text-sm">Opening your story…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md border-border/50">
          <CardContent className="space-y-4 p-8 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-destructive" />
            <h2 className="font-display text-xl font-bold">
              We couldn&apos;t open your report just yet
            </h2>
            <p className="text-sm text-muted-foreground">
              Nothing you answered is lost. This is us, not you — the report is still
              being assembled. Try again in a moment, or go straight to your results.
            </p>
            <div className="grid gap-2">
              <Button
                data-testid="report-retry"
                onClick={() => setAttempt((a) => a + 1)}
                className="h-12 rounded-2xl"
              >
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Try again
              </Button>
              <Button asChild variant="outline" className="h-12 rounded-2xl">
                <Link to="/assessment/v2/result" data-testid="report-fallback-results">
                  View your results
                </Link>
              </Button>
              <Button asChild variant="ghost" className="h-12 rounded-2xl">
                <Link to="/report" data-testid="report-canonical-link" reloadDocument>
                  Open the canonical report URL
                </Link>
              </Button>
              <Button asChild variant="ghost" className="h-12 rounded-2xl">
                <Link to="/start">Back to assessment</Link>
              </Button>
            </div>
            <details className="text-left">
              <summary className="cursor-pointer text-xs text-muted-foreground">
                Technical detail
              </summary>
              <p className="mt-2 break-words font-mono text-xs text-destructive">{error}</p>
            </details>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md border-border/50">
          <CardContent className="p-8 text-center space-y-3">
            <Sparkles className="mx-auto h-10 w-10 text-primary" />
            <h2 className="font-display text-xl font-bold">No report yet</h2>
            <p className="text-sm text-muted-foreground">
              Complete the assessment first — your report will be generated automatically when the pipeline finishes.
            </p>
            <Button asChild><Link to="/start">Take the assessment</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AppShell title="Career DNA™">
      <PageHead
        title="Your Career DNA™ | WeKIT™ Career Clarity 360"
        description="Your Living Report™ — rendered directly from the WeKIT™ Experience Engine."
        path="/report"
        noIndex
      />

      {payload.derived && !(payload as { saved_copy?: unknown }).saved_copy && (
        <p className="mb-5 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
          Reading live from your answers. Every number below traces back to
          something you chose — we&apos;ll seal this version once your full
          record finishes processing.
        </p>
      )}


      {user?.id && <ReportHistory learnerId={user.id} refresh={historyRefresh} onSelect={(p) => {
        setPayload(p); setHistorical(true); setSaveStatus("Viewing a saved version");
      }} />}
      <div className="mb-5 flex flex-wrap justify-end gap-3 print:hidden">
        {historical ? <Button variant="outline" className="h-12" onClick={() => setAttempt(a => a + 1)}>Return to latest report</Button>
          : <Button variant="outline" className="h-12" disabled={saving || sealedPayloadIsThin(payload)} onClick={() => void saveVersion(payload)}>
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />Save new version
          </Button>}
        <Button
          variant="outline"
          className="h-12 rounded-2xl"
          onClick={() => window.print()}
          data-testid="report-print"
        >
          <Printer className="mr-2 h-4 w-4" aria-hidden="true" />
          Print / Save as PDF
        </Button>
        {!historical && <Button asChild className="h-12 rounded-2xl print:hidden"><Link to="/colleges">Find my colleges</Link></Button>}
      </div>
      {saveStatus && <p role="status" className="mb-5 text-sm text-muted-foreground print:hidden">{saveStatus}</p>}
      {sealedPayloadIsThin(payload) && <p role="status" className="mb-5 text-sm text-muted-foreground">This saved report is partial. Missing sections need assessment evidence before a complete version can be saved.</p>}

      <DnaSurface payload={payload} />

      {/* Roadmaps live here too, so they can be saved and exported in one place. */}
      {payload.presentation_snapshot ? <SavedReportRoadmaps payload={payload} /> : !historical && <div className="mt-6 space-y-6">
        <RoadmapPanel kind="journey" compact />
        <RoadmapPanel kind="learning" compact />
      </div>}

      <div className="mt-10 border-t border-border/50 pt-2">
        <LivingReport payload={payload} />
      </div>
      {user?.id && <CollegePlanSection learnerId={user.id} snapshot={historical ? payload.presentation_snapshot?.college_plan : undefined} before={historical ? payload.saved_copy?.saved_at ?? payload.provenance.generated_at : undefined} />}
      {user?.id && <CounsellorRecommendations learnerId={user.id} />}
    </AppShell>
  );
}

